/* Afterburner QA - optional Email on Acid visual QA add-on.
   Runs after main QA and reads live eoa-qa/ artifacts from the backend. */

const abSeverityOrder = ['blocker', 'high', 'medium', 'low'];
const abSeverityLabel = { blocker: 'Blocker', high: 'High', medium: 'Medium', low: 'Low' };
const abSeverityColor = { blocker: 'var(--fail)', high: 'var(--brand-orange)', medium: 'var(--cond)', low: 'var(--queued)' };
const abPhaseLabel = {
  idle: 'Idle',
  queued: 'Queued',
  submitting_to_eoa: 'Submitting',
  fetching_renders: 'Fetching renders',
  stage_1_deterministic: 'Script checks',
  stage_2_perceptron: 'Perceptron',
  stage_3_claude: 'Claude diagnosis',
  complete: 'Complete',
  skipped: 'Skipped',
  timeout: 'Timed out',
  error: 'Error',
  interrupted: 'Interrupted',
};

function abNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function abPercent(value) {
  return Math.max(0, Math.min(100, abNumber(value)));
}

function abRelPath(value) {
  if (!value) return null;
  const text = String(value);
  if (text.startsWith('eoa-qa/')) return text;
  const marker = '/eoa-qa/';
  const idx = text.indexOf(marker);
  if (idx >= 0) return text.slice(idx + 1);
  return null;
}

function abScreenshotRel(render) {
  return render?.screenshot?.relPath
    || abRelPath(render?.screenshot?.path)
    || render?.thumbnail?.relPath
    || abRelPath(render?.thumbnail?.path);
}

function abRenderKey(render) {
  return `${render?.clientId || render?.clientName || ''}::${render?.variant || 'default'}`;
}

function abRows(status) {
  const manifestRows = status?.manifest?.renders || [];
  const stage1Rows = status?.stage1?.renders || [];
  const byKey = new Map(stage1Rows.map((row) => [abRenderKey(row), row]));
  return manifestRows.map((render) => {
    const qa = byKey.get(abRenderKey(render));
    return { ...render, qa, qaStatus: qa?.status || render.status || 'complete' };
  });
}

function abFindingRows(status) {
  const diagnoses = status?.stage3?.diagnoses || [];
  return [...diagnoses].sort((a, b) => {
    const ai = abSeverityOrder.indexOf(a.severity || 'low');
    const bi = abSeverityOrder.indexOf(b.severity || 'low');
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

function abStatusPill(status) {
  if (['queued', 'running'].includes(status)) return <span className="pill pill-run" style={{ marginLeft: 4 }}><span className="dot"></span>Running</span>;
  if (status === 'complete') return <span className="pill pill-pass" style={{ marginLeft: 4 }}><span className="dot"></span>Complete</span>;
  if (status === 'skipped') return <span className="pill pill-cond" style={{ marginLeft: 4 }}><I.warn size={10} />Skipped</span>;
  if (status === 'timeout') return <span className="pill pill-cond" style={{ marginLeft: 4 }}><I.warn size={10} />Timed out</span>;
  if (status === 'error') return <span className="pill pill-fail" style={{ marginLeft: 4 }}><span className="dot"></span>Error</span>;
  return <span className="chip mono" style={{ fontSize: 10.5, marginLeft: 4 }}>Ready</span>;
}

function RenderStatus({ status }) {
  if (status === 'safe' || status === 'complete') return <span className="pill pill-pass" style={{ fontSize: 10.5 }}><span className="dot"></span>Clean</span>;
  if (status === 'suspicious') return <span className="pill pill-cond" style={{ fontSize: 10.5 }}><I.warn size={10} />Suspect</span>;
  if (status === 'obvious_failure' || status === 'error') return <span className="pill pill-fail" style={{ fontSize: 10.5 }}><span className="dot"></span>Broken</span>;
  return <span className="chip mono" style={{ fontSize: 10 }}>{status || 'pending'}</span>;
}

function AbThumb({ job, render }) {
  const c = window.MAV.clientBySlug(job.client);
  const rel = abScreenshotRel(render);
  const src = rel && window.GooseAPI?.artifactUrl ? window.GooseAPI.artifactUrl(job.id, rel) : null;
  const name = render.clientName || render.clientId || 'Client render';
  const status = render.qaStatus || render.status;
  const ring = status === 'obvious_failure' ? 'var(--fail)' : status === 'suspicious' ? 'var(--cond)' : 'transparent';
  const surface = String(render.variant || '').includes('dark') ? '#22242a' : '#ffffff';
  const line = surface === '#22242a' ? 'rgba(255,255,255,0.22)' : '#dcdcdc';

  return (
    <div
      className="ab-thumb"
      onDoubleClick={(e) => window.openRenderLightbox && window.openRenderLightbox(e.currentTarget, name)}
      title="Double-click to enlarge"
      style={ring !== 'transparent' ? { outline: `2px solid ${ring}`, outlineOffset: '-2px' } : null}
    >
      {src && <img data-render={'eoa-' + String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-')} className="rs-img" alt={name} src={src} />}
      <div className="ab-screen" style={{ background: surface }}>
        <div style={{ height: 18, borderRadius: 3, marginBottom: 7, background: c.accent }}></div>
        <div style={{ height: 6, borderRadius: 2, background: line, width: '88%' }}></div>
        <div style={{ height: 6, borderRadius: 2, background: line, width: '62%', marginTop: 5 }}></div>
        <div style={{ height: 14, borderRadius: 3, marginTop: 10, width: '68%', background: c.accent }}></div>
      </div>
    </div>
  );
}

function AfterburnerQA({ status, job, onRefresh }) {
  const current = status || { status: 'not_started', phase: 'idle', progress: 0 };
  const summary = current.summary || {};
  const eoa = current.eoa || current.eoaResult || {};
  const renders = abRows(current);
  const findings = abFindingRows(current);
  const sevCounts = abSeverityOrder.map((sev) => [sev, findings.filter((item) => (item.severity || 'low') === sev).length]);
  const total = abNumber(summary.totalRenders || eoa.screenshotCount || renders.length);
  const suspicious = abNumber(summary.suspicious);
  const perceptron = abNumber(summary.sentToPerceptron ?? summary.perceptronDecisions ?? current.stage2?.decisions?.length ?? suspicious);
  const claude = abNumber(summary.sentToClaude ?? summary.claudeDiagnoses ?? findings.length);
  const human = findings.filter((item) => item.needsHumanReview || item.humanReview).length;
  const reportUrl = window.GooseAPI?.artifactUrl ? window.GooseAPI.artifactUrl(job.id, 'eoa-qa/eoa-visual-qa-report.md') : '#';
  const phase = abPhaseLabel[current.phase] || current.phase || 'Idle';
  const running = ['queued', 'running'].includes(current.status);
  const reason = current.error || eoa.reason || current.eoaResult?.reason || '';
  const byClient = window.MAV.clientBySlug(job.client);

  const cascade = [
    { v: total, k: 'Script checks', of: 'all renders', ico: I.layers },
    { v: perceptron, k: 'Perceptron Mk1', of: 'suspicious', ico: I.review },
    { v: claude, k: 'Claude diagnosis', of: 'likely broken', ico: I.bolt },
    { v: human, k: 'Human review', of: 'low-confidence', ico: I.warn },
  ];

  return (
    <div className="card card-pad">
      <div className="ab-head">
        <span className="ab-title"><span className="ab-flame"><I.flame size={18} /></span> Afterburner QA {abStatusPill(current.status)}</span>
        <div className="row gap-8">
          <span className="muted mono" style={{ fontSize: 12, alignSelf: 'center' }}>Email on Acid · {byClient.name}</span>
          {onRefresh && <button className="btn btn-ghost" style={{ padding: '7px 13px', fontSize: 12.5 }} onClick={onRefresh}><I.retry size={14} /> Refresh</button>}
          {current.report && <a className="btn btn-ghost" style={{ padding: '7px 13px', fontSize: 12.5 }} href={reportUrl} target="_blank" rel="noopener noreferrer"><I.download size={14} /> eoa-qa report</a>}
        </div>
      </div>

      {running && (
        <React.Fragment>
          <div style={{ height: 9, borderRadius: 999, background: 'var(--sink)', boxShadow: 'var(--nm-in-sm)', overflow: 'hidden', marginBottom: 14 }}>
            <span style={{ display: 'block', height: '100%', width: abPercent(current.progress) + '%', background: 'var(--pass)', borderRadius: 999 }}></span>
          </div>
          <div className="logwrap" style={{ maxHeight: 130, marginBottom: 18 }}>
            <div className="log-line"><span className="ts">{String(Math.round(abPercent(current.progress))).padStart(2, '0')}% </span><span className="info">{phase}</span></div>
            {current.startedAt && <div className="log-line"><span className="ts">start </span><span>{new Date(current.startedAt).toLocaleString()}</span></div>}
            <div className="log-line"><span className="cur">█</span></div>
          </div>
        </React.Fragment>
      )}

      {!running && reason && (
        <div className={'blocker ' + (current.status === 'error' ? '' : 'cond')} style={{ marginBottom: 18 }}>
          <span className="bi"><I.warn size={20} /></span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{phase}</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{reason}</div>
          </div>
        </div>
      )}

      <div className="cascade">
        {cascade.map((node, i) => (
          <React.Fragment key={node.k}>
            <div className="casc-node">
              <div className="casc-v">{node.v}</div>
              <div className="casc-k"><node.ico size={13} /> {node.k}</div>
              <div className="casc-of">of {node.of}</div>
            </div>
            {i < cascade.length - 1 && <div className="casc-arrow"><I.chevron size={16} /></div>}
          </React.Fragment>
        ))}
      </div>

      <div className="ab-sev-row mt-20">
        {sevCounts.map(([sev, count]) => (
          <div key={sev} className="ab-sev-chip"><span className="dot" style={{ background: abSeverityColor[sev] }}></span>{abSeverityLabel[sev]}<span className="n" style={{ color: abSeverityColor[sev] }}>{count}</span></div>
        ))}
        <div className="ab-sev-chip" style={{ marginLeft: 'auto' }}><I.warn size={13} style={{ color: 'var(--cond)' }} /> {human} routed to human review</div>
      </div>

      {renders.length > 0 && (
        <React.Fragment>
          <div className="sec-head mt-24" style={{ marginBottom: 14 }}><span className="sec-title">Client renders</span><span className="muted" style={{ fontSize: 12 }}>{Math.min(renders.length, 120)} of {total || renders.length} shown</span></div>
          <div className="ab-render-grid">
            {renders.slice(0, 120).map((render, i) => (
              <div key={abRenderKey(render) + i} className="ab-render">
                <AbThumb job={job} render={render} />
                <div className="arc-name">{render.clientName || render.clientId}</div>
                <div className="arc-fam">{render.clientFamily || 'client'}{render.variant ? ' · ' + render.variant : ''}</div>
                <div className="arc-foot"><RenderStatus status={render.qaStatus} /></div>
              </div>
            ))}
          </div>
        </React.Fragment>
      )}

      {findings.length > 0 && (
        <React.Fragment>
          <div className="sec-head mt-24" style={{ marginBottom: 14 }}><span className="sec-title">Findings</span><span className="muted" style={{ fontSize: 12 }}>{findings.length} diagnosis rows</span></div>
          {findings.map((item, i) => {
            const sev = abSeverityOrder.includes(item.severity) ? item.severity : 'low';
            return (
              <div key={i} className={'ab-finding sev-' + sev}>
                <span className="ab-sevbadge">{abSeverityLabel[sev]}</span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row gap-8 wrap" style={{ alignItems: 'center' }}>
                    <span className="ab-client">{item.clientName || item.clientId || 'Client'}</span>
                    {item.type && <span className="chip" style={{ fontSize: 10.5 }}>{item.type}</span>}
                    {item.confidence != null && <span className="chip mono" style={{ fontSize: 10.5 }}>conf {Math.round(Number(item.confidence) * 100)}%</span>}
                    {(item.needsHumanReview || item.humanReview) && <span className="pill pill-cond" style={{ fontSize: 10.5 }}><I.warn size={10} />Human review</span>}
                  </div>
                  <div className="ab-sum">{item.summary || item.reason || 'Diagnosis recorded.'}</div>
                  {item.fix && <div className="ab-fix"><span className="ab-fix-k">Suggested fix · </span>{item.fix}</div>}
                </div>
              </div>
            );
          })}
        </React.Fragment>
      )}

      {!running && !renders.length && !findings.length && !reason && (
        <p className="muted" style={{ fontSize: 12.5, margin: '16px 0 0' }}>No Afterburner artifacts have been produced for this build yet.</p>
      )}
    </div>
  );
}

/* ── Afterburner intake-method chooser modal ──────────────────────────────────
   Opens when the dev clicks "Afterburner QA" — choose how the render reaches the
   Email-on-Acid triage cascade:
     • Connected API (default) — submit the build's own HTML (sourceHtmlPath → submitToEoa)
     • Paste a link            — backend downloads the asset → 1-entry manifest → same stages
   onRun({ method, url }) is called by jobdetail's runAb. */
function AfterburnerIntake({ job, onClose, onRun }) {
  const MAV = window.MAV;
  const client = job ? MAV.clientBySlug(job.client) : null;
  const [method, setMethod] = React.useState('api');   // 'api' | 'link'
  const [url, setUrl] = React.useState('');
  const validUrl = /^https?:\/\/[^\s.]+\.[^\s]{2,}/.test(url.trim());
  const canRun = method === 'api' ? true : validUrl;

  React.useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, []);

  const ell = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}>
              <span className="chip" style={{ background: 'var(--pass)', color: '#fff', fontWeight: 700 }}><I.flame size={12} fill="currentColor" /> Afterburner</span>
              <span className="chip">Image intake</span>
            </div>
            <h2 className="fb-title">Run Afterburner QA</h2>
            <p className="fb-sub">Choose how the render reaches Email on Acid for cross-client diffing.</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>

        <div className="fb-body">
          {job && (
            <div className="ri-context">
              <ClientBadge slug={job.client} size={30} />
              <div style={{ minWidth: 0 }}>
                <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, ...ell }}>{job.name}</div>
                <div className="muted" style={{ fontSize: 11 }}>{client ? client.name : 'Unassigned'} · {job.id}</div>
              </div>
            </div>
          )}

          <div className="fb-section" style={{ paddingTop: 4 }}>
            <span className="fb-q">Intake method</span>
            <div className="abi-methods">
              <button type="button" className={'abi-method' + (method === 'api' ? ' active' : '')} onClick={() => setMethod('api')}>
                <span className="abi-ico"><I.bolt size={18} /></span>
                <div className="abi-meta">
                  <div className="abi-name">Connected API <span className="chip" style={{ fontSize: 9.5 }}>Fastest</span></div>
                  <div className="abi-desc">Submits the build's generated HTML straight to Email on Acid — the default live path (sourceHtmlPath → submitToEoa).</div>
                </div>
                <span className="abi-radio">{method === 'api' && <I.check size={12} />}</span>
              </button>
              <button type="button" className={'abi-method' + (method === 'link' ? ' active' : '')} onClick={() => setMethod('link')}>
                <span className="abi-ico"><I.link size={18} /></span>
                <div className="abi-meta">
                  <div className="abi-name">Paste a link</div>
                  <div className="abi-desc">Drop a hosted preview or image URL. The backend extractor downloads the render and feeds it to the triage cascade.</div>
                </div>
                <span className="abi-radio">{method === 'link' && <I.check size={12} />}</span>
              </button>
            </div>
          </div>

          {method === 'api' && (
            <div className="abi-detail">
              <div className="row gap-8" style={{ alignItems: 'center' }}>
                <span className="abi-detail-ico ok"><I.check size={13} /></span>
                <span style={{ fontSize: 12.5 }}>Uses the build's own HTML · artifacts land under <span className="mono">eoa-qa/</span> as today.</span>
              </div>
            </div>
          )}

          {method === 'link' && (
            <div className="abi-detail">
              <label className="field-lbl">Preview or image URL</label>
              <div className={'abi-url' + (url.trim() && !validUrl ? ' bad' : '')}>
                <I.link size={14} />
                <input className="abi-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://previews.host/build-1186  ·  https://cdn.host/render.png" autoFocus />
                {validUrl && <I.check size={15} style={{ color: 'var(--pass)', flexShrink: 0 }} />}
              </div>
              {url.trim() && !validUrl && <p className="abi-warn"><I.warn size={12} /> Enter a full http(s) URL.</p>}
              <div className="abi-flow">
                <span className="abi-flow-step"><I.link size={12} /> Link</span>
                <I.chevron size={13} />
                <span className="abi-flow-step"><I.image size={12} /> Extractor pulls image</span>
                <I.chevron size={13} />
                <span className="abi-flow-step"><I.flame size={12} /> Afterburner</span>
              </div>
              <p className="muted" style={{ fontSize: 11, marginTop: 9, lineHeight: 1.5 }}>Backend downloads &amp; normalizes the asset (reusing <span className="mono">downloadAsset()</span>), writes a one-entry render-manifest, then runs the same stage 1–3 cascade.</p>
            </div>
          )}
        </div>

        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Optional QA · never gates delivery</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
            <button className="btn btn-after btn-lg" onClick={() => onRun({ method, url: method === 'link' ? url.trim() : null })} disabled={!canRun}
              title={canRun ? 'Start Afterburner QA' : 'Paste a valid URL first'}>
              <I.flame size={15} fill="currentColor" /> {method === 'link' ? 'Extract & run' : 'Run Afterburner'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, { AfterburnerQA, AfterburnerIntake });
